import React, { useState, useEffect } from 'react';
import './DepositManager.css';
import ApiService from '../services/api';
import MetaMaskService from '../services/metamask';

const DepositManager = ({ onClose }) => {
  const [depositAmount, setDepositAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: Amount, 2: Confirm, 3: Processing, 4: Complete
  const [depositData, setDepositData] = useState(null);
  const [autoDeposit, setAutoDeposit] = useState(true); // 🚀 DÉPÔT AUTOMATIQUE PAR DÉFAUT
  const [userBalance, setUserBalance] = useState({
    eth: '0',
    usdc: '0'
  });

  // Récupérer la balance utilisateur au chargement
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const address = await MetaMaskService.getCurrentAddress();
        if (address) {
          const ethBalance = await MetaMaskService.getBalance(address);
          setUserBalance(prev => ({
            ...prev,
            eth: ethBalance
          }));
          
          // TODO: Récupérer balance USDC via contrat
          // const usdcBalance = await getUSDCBalance(address);
          setUserBalance(prev => ({
            ...prev,
            usdc: '1250.75' // Simulé pour l'instant
          }));
        }
      } catch (error) {
        console.error('Erreur récupération balance:', error);
      }
    };

    fetchBalance();
  }, []);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setDepositAmount(value);
      setError('');
    }
  };

  const validateAmount = () => {
    const amount = parseFloat(depositAmount);
    const available = parseFloat(userBalance.usdc);
    
    if (!amount || amount <= 0) {
      setError('Montant invalide');
      return false;
    }
    
    if (amount > available) {
      setError('Solde USDC insuffisant');
      return false;
    }
    
    if (amount < 10) {
      setError('Montant minimum: 10 USDC');
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateAmount()) {
      setStep(2);
    }
  };

  const handleConfirmDeposit = async () => {
    setIsProcessing(true);
    setError('');
    setStep(3);

    try {
      // 🚀 DÉPÔT 100% AUTOMATISÉ - SANS INTERFACE HYPERLIQUID !
      console.log(`🚀 Dépôt ${autoDeposit ? 'AUTOMATIQUE' : 'MANUEL'}: ${depositAmount} USDC`);
      
      const result = await ApiService.initiateDeposit(
        parseFloat(depositAmount),
        autoDeposit // Paramètre pour activer l'automation
      );
      
      if (result.success) {
        setDepositData(result.deposit);
        setStep(4);
        
        if (result.deposit.automated) {
          setSuccess(`✅ ${depositAmount} USDC déposés automatiquement ! Transaction: ${result.deposit.txHash?.substring(0, 10)}...`);
        } else {
          setSuccess(`Dépôt de ${depositAmount} USDC initié avec succès !`);
        }
      } else {
        throw new Error(result.error || 'Erreur initiation dépôt');
      }
    } catch (error) {
      console.error('Erreur dépôt:', error);
      setError(error.message || 'Erreur lors du dépôt');
      setStep(2);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="deposit-step">
            <h3>💰 Montant du Dépôt</h3>
            <p>Déposez des USDC sur votre compte Hyperliquid</p>
            
            <div className="balance-info">
              <div className="balance-item">
                <span className="label">Balance USDC:</span>
                <span className="value">{userBalance.usdc} USDC</span>
              </div>
              <div className="balance-item">
                <span className="label">Balance ETH:</span>
                <span className="value">{parseFloat(userBalance.eth).toFixed(4)} ETH</span>
              </div>
            </div>

            <div className="amount-input-section">
              <label htmlFor="depositAmount">Montant à déposer (USDC)</label>
              <div className="input-with-max">
                <input
                  type="text"
                  id="depositAmount"
                  value={depositAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  disabled={isProcessing}
                />
                <button 
                  className="max-button"
                  onClick={() => setDepositAmount(userBalance.usdc)}
                  disabled={isProcessing}
                >
                  MAX
                </button>
              </div>
            </div>

            <div className="deposit-options">
              <label className="checkbox-label auto-deposit">
                <input
                  type="checkbox"
                  checked={autoDeposit}
                  onChange={(e) => setAutoDeposit(e.target.checked)}
                />
                <span className="checkmark"></span>
                <div className="option-details">
                  <span className="option-title">🚀 Dépôt 100% Automatique</span>
                  <span className="option-description">
                    {autoDeposit ? 
                      'Dépôt direct sans passer par Hyperliquid ✅' : 
                      'Dépôt manuel via instructions étape par étape'
                    }
                  </span>
                </div>
              </label>
            </div>

            <div className="quick-amounts">
              <span className="quick-label">Montants rapides:</span>
              {['100', '500', '1000'].map(amount => (
                <button
                  key={amount}
                  className="quick-amount-btn"
                  onClick={() => setDepositAmount(amount)}
                  disabled={isProcessing}
                >
                  {amount} USDC
                </button>
              ))}
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <div className="step-actions">
              <button className="cancel-btn" onClick={onClose}>
                Annuler
              </button>
              <button 
                className="next-btn"
                onClick={handleNext}
                disabled={!depositAmount || isProcessing}
              >
                Continuer
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="deposit-step">
            <h3>✅ Confirmation du Dépôt</h3>
            <p>Vérifiez les détails de votre dépôt</p>
            
            <div className="confirmation-details">
              <div className="detail-row">
                <span className="label">Montant:</span>
                <span className="value">{depositAmount} USDC</span>
              </div>
              <div className="detail-row">
                <span className="label">Destination:</span>
                <span className="value">Hyperliquid Trading Account</span>
              </div>
              <div className="detail-row">
                <span className="label">Frais estimés:</span>
                <span className="value">~0.001 ETH (Gas)</span>
              </div>
            </div>

            <div className="warning-box">
              <h4>⚠️ Important</h4>
              <ul>
                <li>Assurez-vous d'avoir suffisamment d'ETH pour les frais de gas</li>
                <li>Le dépôt sera traité en 2 transactions</li>
                <li>Les fonds apparaîtront sur Hyperliquid sous 5-10 minutes</li>
              </ul>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <div className="step-actions">
              <button className="back-btn" onClick={() => setStep(1)}>
                Retour
              </button>
              <button 
                className="confirm-btn"
                onClick={handleConfirmDeposit}
                disabled={isProcessing}
              >
                Confirmer le Dépôt
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="deposit-step processing">
            <h3>⏳ Traitement en Cours</h3>
            <div className="processing-animation">
              <div className="spinner-large"></div>
            </div>
            <p>Initiation du dépôt de {depositAmount} USDC...</p>
            <div className="processing-steps">
              <div className="process-step active">
                <span className="step-number">1</span>
                <span className="step-text">Préparation de la transaction</span>
              </div>
              <div className="process-step">
                <span className="step-number">2</span>
                <span className="step-text">Approbation USDC</span>
              </div>
              <div className="process-step">
                <span className="step-number">3</span>
                <span className="step-text">Transfert vers Hyperliquid</span>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="deposit-step success">
            <h3>🎉 Dépôt Initié !</h3>
            <div className="success-icon">✅</div>
            
            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

            {depositData && (
              <div className="next-steps">
                <h4>📋 Prochaines Étapes</h4>
                <ol>
                  <li>
                    <strong>Approuver le contrat USDC</strong>
                    <br />
                    <small>Autorisez le transfert de vos USDC</small>
                  </li>
                  <li>
                    <strong>Confirmer le transfert</strong>
                    <br />
                    <small>Signez la transaction de dépôt</small>
                  </li>
                  <li>
                    <strong>Attendre la confirmation</strong>
                    <br />
                    <small>Les fonds apparaîtront sur Hyperliquid sous 5-10 minutes</small>
                  </li>
                </ol>

                <div className="contract-info">
                  <h5>📄 Informations Contrats</h5>
                  <div className="contract-item">
                    <span className="label">USDC:</span>
                    <span className="address">{depositData.instructions?.contractAddress}</span>
                  </div>
                  <div className="contract-item">
                    <span className="label">Hyperliquid:</span>
                    <span className="address">{depositData.instructions?.hyperliquidContract}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="step-actions">
              <button className="close-btn" onClick={onClose}>
                Fermer
              </button>
              <button 
                className="view-portfolio-btn"
                onClick={() => {
                  onClose();
                  // Rediriger vers le portfolio
                }}
              >
                Voir le Portfolio
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="deposit-manager-overlay">
      <div className="deposit-manager">
        <div className="deposit-header">
          <h2>💸 Dépôt Hyperliquid</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="deposit-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(step / 4) * 100}%` }}
            ></div>
          </div>
          <div className="progress-steps">
            {[1, 2, 3, 4].map(stepNum => (
              <div 
                key={stepNum}
                className={`progress-step ${step >= stepNum ? 'active' : ''}`}
              >
                {stepNum}
              </div>
            ))}
          </div>
        </div>

        <div className="deposit-content">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default DepositManager;
